<template>
    <div class="flex flex-col max-h-full h-full pt-4 pb-4 bg-white dark:bg-zinc-900 p-3.5 sm:p-4 border-r border-gray-200 dark:border-zinc-800 md:w-[300px] z-[30] select-none" >
        <!-- Header & Action Toolbar -->
        <div class="flex flex-col gap-1" :class="{'flex-col': editingTitle}">
            <div class="text-sm font-bold text-slate-800 dark:text-zinc-100 max-w-full" @dblclick="editTitle()" title="Double click to edit title">
                <span v-if="!editingTitle" class="block truncate">{{ props.title }}</span>
                <input type="text" v-if="editingTitle" 
                    class="text-sm font-bold text-slate-800 dark:text-zinc-100 p-1.5 border-none border-b-2 border-b-indigo-500 bg-transparent rounded-md w-full outline-none" 
                    @blur="saveTitle($event)" :value="props.title" @keydown.enter="saveTitle($event)" autofocus />
            </div>
            <div class="flex flex-row items-center gap-1.5 pb-2">
                <Tooltip :text="pageMode === 'slim' ? ($t('presentation.modePreview') || 'Switch to Preview mode') : ($t('presentation.modeSlim') || 'Switch to Compact list mode')" position="bottom">
                    <button type="button" class="flex items-center justify-center size-8 rounded-xl text-slate-500 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-slate-100 dark:hover:bg-zinc-800 transition-all cursor-pointer" 
                        @click="togglePageMode()">
                        <Transition name="slide-up" mode="out-in">
                            <QueueListIcon v-if="pageMode === 'slim'" class="w-4 h-4" />
                            <TvIcon v-else class="w-4 h-4" />
                        </Transition>
                    </button>
                </Tooltip>
                <Tooltip :text="$t('presentation.addNewPage') || 'Add new slide'" position="bottom">
                    <button type="button" class="flex items-center justify-center size-8 rounded-xl text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/50 transition-all cursor-pointer" 
                        @click="addNewPage(currentPage)">
                        <PlusIcon class="w-4 h-4" />
                    </button>
                </Tooltip>
                <Tooltip :text="$t('presentation.duplicatePage') || 'Duplicate slide'" position="bottom">
                    <button type="button" :disabled="!groupedPages[currentPage]?.editable"
                        class="flex items-center justify-center size-8 rounded-xl text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/50 transition-all cursor-pointer" 
                        :class="{'opacity-25 !cursor-not-allowed': !groupedPages[currentPage]?.editable}"
                        @click="duplicatePage(currentPage)">
                        <DocumentDuplicateIcon class="w-4 h-4" />
                    </button>
                </Tooltip>
                <Tooltip :text="$t('presentation.skipPage') || 'Skip slide during presentation'" position="bottom">
                    <button type="button"
                        class="flex items-center justify-center size-8 rounded-xl transition-all cursor-pointer"
                        :class="groupedPages[currentPage]?.skipped
                            ? 'text-amber-500 hover:text-amber-600 bg-amber-50 dark:bg-amber-950/40'
                            : 'text-slate-500 hover:text-slate-700 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-slate-100 dark:hover:bg-zinc-800'"
                        :style="!groupedPages[currentPage]?.editable ? 'opacity:.25;cursor:not-allowed' : ''"
                        @click="toggleSkip(currentPage)">
                        <EyeSlashIcon class="w-4 h-4" />
                    </button>
                </Tooltip>
                <Tooltip :text="$t('presentation.removePage') || 'Remove slide'" position="bottom">
                    <button type="button" :disabled="currentPage === 0" 
                        class="flex items-center justify-center size-8 rounded-xl text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50 transition-all cursor-pointer" 
                        :class="{'opacity-25 !cursor-not-allowed': currentPage === 0}"
                        @click="removePage(currentPage)">
                        <TrashIcon class="w-4 h-4" />
                    </button>
                </Tooltip>
            </div>
        </div>

        <!-- Sticky Quick Category Jump Chips (wraps neatly so all song categories appear clearly) -->
        <div v-if="songCategories.length > 0" class="flex flex-wrap items-center gap-1.5 py-2 mb-2 flex-shrink-0 border-y border-gray-100 dark:border-zinc-800/80">
            <button
                v-for="cat in songCategories"
                :key="cat.id"
                type="button"
                @click.stop="jumpToCategory(cat.id)"
                :title="`${$t(cat.labelKey) || cat.defaultLabel} [${cat.shortcutKey.toUpperCase()}] (${cat.count || 1})`"
                class="px-2 py-1 rounded-lg text-xs font-bold border transition-all cursor-pointer flex items-center gap-1.5 active:scale-95 select-none shadow-2xs"
                :class="groupedPages[currentPage]?.category === cat.id ? cat.activePillClass : cat.pillClass"
            >
                <span class="w-1.5 h-1.5 rounded-full shrink-0" :class="cat.dotClass"></span>
                <span>{{ $t(cat.labelKey) || cat.defaultLabel }}</span>
                <span class="text-[9px] opacity-75 font-mono">[{{ cat.shortcutKey.toUpperCase() }}]</span>
            </button>
        </div>

        <!-- Slide Pages Scroll Container -->
        <div ref="scrollContainer" class="flex-1 overflow-y-auto overflow-x-hidden pr-0.5">
            <div ref="pagesContainer" class="flex flex-col gap-2.5 w-full pb-6">
                <div
                    v-for="(page, idx) in groupedPages"
                    :key="`page-${idx}-${reorderKey}-${page?.content?.substring(0, 20) || idx}`" 
                    :ref="el => { if (el) pageRefs[idx] = el }"
                    @click="selectPage(idx)"
                    class="group relative flex flex-row gap-2 items-start rounded-xl cursor-pointer p-2 transition-all duration-150 border"
                    :class="[
                        idx === currentPage
                            ? 'bg-indigo-50/60 dark:bg-zinc-800/90 ring-2 ring-indigo-500 dark:ring-indigo-400 border-indigo-300 dark:border-indigo-600 shadow-sm'
                            : 'bg-white dark:bg-zinc-900/60 hover:bg-slate-50 dark:hover:bg-zinc-800/50 border-gray-200 dark:border-zinc-800 shadow-2xs',
                        page.category && getCategoryConfig(page.category)
                            ? `${getCategoryConfig(page.category).leftBorderClass} border-l-[4px]`
                            : 'border-l-slate-300 dark:border-l-zinc-700 border-l-[4px]',
                        idx === 0 ? 'no-drag' : '',
                        page?.skipped ? 'opacity-40' : '',
                    ]"
                >
                    <!-- Slide Number -->
                    <div class="w-5 shrink-0 pt-0.5 text-xs font-bold text-slate-400 dark:text-zinc-500 text-center select-none font-mono">
                        {{ idx + 1 }}
                    </div>

                    <!-- Slide Body -->
                    <div class="flex-1 min-w-0 flex flex-col">
                        <!-- Slide Sub-header: category chip & drag handle -->
                        <div class="flex items-center justify-between mb-1.5 min-h-[22px]">
                            <div class="flex items-center gap-1.5 flex-wrap">
                                <!-- Blank transition slide indicator -->
                                <span
                                    v-if="!page.editable"
                                    class="px-1.5 py-0.5 rounded-md bg-slate-800 dark:bg-zinc-700 text-white text-[10px] font-bold uppercase tracking-wider"
                                >
                                    {{ $t('presentation.blankBadge') || 'Blank' }}
                                </span>

                                <!-- Category Dropdown Popover Trigger -->
                                <div v-else class="relative inline-block" @click.stop>
                                    <!-- Assigned Category Pill -->
                                    <button
                                        v-if="page.category && getCategoryConfig(page.category)"
                                        type="button"
                                        @click.stop="toggleCategoryMenu(idx)"
                                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold border tracking-tight transition-all hover:opacity-90 active:scale-95 cursor-pointer shadow-2xs"
                                        :class="getCategoryConfig(page.category)?.chipClass"
                                        :title="$t('presentation.changeCategory') || 'Change category'"
                                    >
                                        <span class="w-1.5 h-1.5 rounded-full shrink-0" :class="getCategoryConfig(page.category)?.dotClass"></span>
                                        <span>{{ $t(getCategoryConfig(page.category)?.labelKey) || getCategoryConfig(page.category)?.defaultLabel }}</span>
                                        <span class="text-[9px] opacity-75 font-mono">[{{ getCategoryConfig(page.category)?.shortcutKey.toUpperCase() }}]</span>
                                        <ChevronDownIcon class="w-3 h-3 ml-0.5 opacity-60 shrink-0" />
                                    </button>

                                    <!-- Unassigned Category Minimal Button -->
                                    <button
                                        v-else
                                        type="button"
                                        @click.stop="toggleCategoryMenu(idx)"
                                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium text-slate-400 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-200 hover:bg-slate-100 dark:hover:bg-zinc-800 transition-all cursor-pointer"
                                        :title="$t('presentation.assignCategory') || 'Categorize slide'"
                                    >
                                        <TagIcon class="w-3 h-3 shrink-0" />
                                        <span>{{ $t('presentation.categorize') || 'Categoria' }}</span>
                                        <ChevronDownIcon class="w-2.5 h-2.5 opacity-60 shrink-0" />
                                    </button>

                                    <!-- Category Popover Dropdown Menu -->
                                    <div
                                        v-if="activeCategoryMenuIdx === idx"
                                        class="absolute left-0 top-full mt-1 z-[120] w-48 bg-white dark:bg-zinc-800 rounded-xl shadow-xl border border-gray-200 dark:border-zinc-700 py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-100 text-left"
                                    >
                                        <div class="px-2.5 py-1 text-[10px] font-bold text-gray-400 dark:text-zinc-400 uppercase tracking-wider border-b border-gray-100 dark:border-zinc-700">
                                            {{ $t('presentation.chooseCategory') || 'Categoria do Slide' }}
                                        </div>
                                        <div class="max-h-60 overflow-y-auto py-1">
                                            <!-- None / Clear -->
                                            <button
                                                type="button"
                                                @click.stop="setCategoryAndClose(idx, '')"
                                                class="w-full text-left px-2.5 py-1.5 text-xs flex items-center justify-between hover:bg-gray-100 dark:hover:bg-zinc-700 text-gray-700 dark:text-zinc-200 cursor-pointer transition-colors"
                                            >
                                                <span class="text-gray-500 dark:text-zinc-400 italic">{{ $t('presentation.catNone') || 'Geral' }}</span>
                                                <CheckIcon v-if="!page.category" class="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
                                            </button>
                                            <!-- Category list -->
                                            <button
                                                v-for="catOpt in SLIDE_CATEGORIES"
                                                :key="catOpt.id"
                                                type="button"
                                                @click.stop="setCategoryAndClose(idx, catOpt.id)"
                                                class="w-full text-left px-2.5 py-1.5 text-xs flex items-center justify-between hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors cursor-pointer"
                                                :class="page.category === catOpt.id ? 'font-bold text-gray-900 dark:text-white bg-slate-50 dark:bg-zinc-750' : 'text-gray-700 dark:text-zinc-300'"
                                            >
                                                <div class="flex items-center gap-2">
                                                    <span class="w-2.5 h-2.5 rounded-full shrink-0" :class="catOpt.dotClass"></span>
                                                    <span>{{ $t(catOpt.labelKey) || catOpt.defaultLabel }}</span>
                                                    <span class="text-[10px] font-mono opacity-60">[{{ catOpt.shortcutKey.toUpperCase() }}]</span>
                                                </div>
                                                <CheckIcon v-if="page.category === catOpt.id" class="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Skipped indicator -->
                                <span
                                    v-if="page?.skipped"
                                    class="px-1.5 py-0.5 rounded-md bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wide"
                                >
                                    {{ $t('presentation.skipped') || 'Ignorado' }}
                                </span>
                            </div>

                            <!-- Drag handle -->
                            <span v-if="idx > 0" class="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:text-zinc-500 dark:hover:text-zinc-300 p-0.5" data-drag-handle="true" title="Drag to reorder">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                                </svg>
                            </span>
                        </div>

                        <!-- Slide Content Preview Container -->
                        <PreviewPageContainer v-if="pageMode === 'preview'" :page="page" :isActive="idx === currentPage" :songBackground="props.songBackground" :songBackgroundAdjust="props.songBackgroundAdjust" :screen="props.screen" />
                        <SlimPageContainer v-if="pageMode === 'slim'" :page="page" :isActive="idx === currentPage" />
                    </div>
                </div>
            </div>
        </div>
        <p class="mt-2 text-[11px] text-gray-400 dark:text-zinc-500">Drag to reorder pages.</p>
    </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits, defineExpose, onMounted, onUnmounted, nextTick } from 'vue';
import Sortable from 'sortablejs';
import { PlusIcon, TrashIcon, QueueListIcon, TvIcon, DocumentDuplicateIcon, EyeSlashIcon, TagIcon, ChevronDownIcon, CheckIcon } from '@heroicons/vue/24/outline';
import PreviewPageContainer from './PreviewPageContainer.vue';
import SlimPageContainer from './SlimPageContainer.vue';
import { SLIDE_CATEGORIES, getCategoryConfig, getSongCategories, findNextSlideByCategory } from '@/utils/slideCategories';

const props = defineProps({
    title: {
        type: String,
        required: true,
        default: 'Presentation'
    },
    pages: {
        type: Array,
        required: true,
        default: () => []
    },
    data: {
        type: Object,
        required: true,
        default: () => {}
    },
    songBackground: {
        type: String,
        default: null
    },
    songBackgroundAdjust: {
        type: Object,
        default: null
    },
    screen: {
        type: String,
        default: '4:3'
    },
    currentPageIndex: {
        type: Number,
        required: true,
        default: 0
    }
});

const groupedPages = ref([]);
const scrollContainer = ref(null);
const pagesContainer = ref(null);
const pageRefs = ref({});
let sortableInstance = null;
const reorderKey = ref(0);
const currentPage = ref(0);
const editingTitle = ref(false);
const activeCategoryMenuIdx = ref(null);

const songCategories = computed(() => getSongCategories(groupedPages.value));

const pageModeOptions = ref([
    { label: 'Preview', value: 'preview' },
    { label: 'Slim View', value: 'slim' }
]);

const pageMode = ref(pageModeOptions.value[0].value);

const emit = defineEmits(['select', 'back-to-previous', 'go-to-next', 'reorder', 'onNewPage', 'onRemovePage', 'onDuplicatePage', 'onToggleSkip', 'update:title', 'update:category']);

const toggleCategoryMenu = (idx) => {
    activeCategoryMenuIdx.value = activeCategoryMenuIdx.value === idx ? null : idx;
};

const closeCategoryMenu = () => {
    activeCategoryMenuIdx.value = null;
};

const setCategoryAndClose = (idx, categoryId) => {
    updatePageCategory(idx, categoryId);
    activeCategoryMenuIdx.value = null;
};

const jumpToCategory = (categoryId) => {
    const nextIdx = findNextSlideByCategory(groupedPages.value, currentPage.value, categoryId);
    if (nextIdx !== null) {
        selectPage(nextIdx);
    }
};

const updatePageCategory = (idx, category) => {
    if (groupedPages.value[idx]) {
        groupedPages.value[idx].category = category || undefined;
        emit('update:category', { index: idx, category, page: groupedPages.value[idx] });
    }
};

const scrollToPage = async (idx) => {
    await nextTick();
    const pageElement = pageRefs.value[idx];
    if (pageElement && scrollContainer.value) {
        pageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
        });
    }
};

const selectPage = async (idx) => {
    currentPage.value = idx;
    emit('select', idx, groupedPages.value[idx], props.data);
    await scrollToPage(idx);
};

watch(() => props.pages, async (newPages) => {
    groupedPages.value = newPages || [];
    const validIndex = Math.min(Math.max(0, props.currentPageIndex ?? currentPage.value), Math.max(0, (newPages?.length || 1) - 1));
    currentPage.value = validIndex;
    await nextTick();
    if (groupedPages.value && groupedPages.value.length > 0) {
        await scrollToPage(validIndex);
    }
    if (sortableInstance) {
        sortableInstance.destroy();
    }
    initSortable();
});

onMounted(() => {
    groupedPages.value = props.pages;
    initSortable();
    window.addEventListener('click', closeCategoryMenu);
});

onUnmounted(() => {
    if (sortableInstance) {
        sortableInstance.destroy();
    }
    window.removeEventListener('click', closeCategoryMenu);
});

const initSortable = () => {
    nextTick(() => {
        if (pagesContainer.value) {
            sortableInstance = Sortable.create(pagesContainer.value, {
                animation: 150,
                handle: '[data-drag-handle]',
                filter: '.no-drag',
                forceFallback: false,
                onEnd: async (evt) => {
                    const { oldIndex, newIndex } = evt;
                    if (oldIndex !== newIndex) {
                        emit('reorder', { oldIndex, newIndex });
                    }
                }
            });
        }
    });
};

const addNewPage = (idx) => {
    if (currentPage.value === idx) {
        currentPage.value = idx + 1;
    }
    emit('onNewPage', idx);
};

const duplicatePage = (idx) => {
    if (!groupedPages.value[idx]?.editable) return;
    emit('onDuplicatePage', idx);
};

const toggleSkip = (idx) => {
    if (!groupedPages.value[idx]?.editable) return;
    emit('onToggleSkip', idx);
};

const removePage = (idx) => {
    if (idx === 0) {
        return;
    }
    if (confirm("Are you sure you want to delete this page?")) {
        emit('onRemovePage', idx);
    }
};

watch(() => props.currentPageIndex, async (newVal) => {
    if (newVal !== undefined && newVal !== null && newVal !== currentPage.value) {
        currentPage.value = newVal;
        await scrollToPage(newVal);
    }
});

const togglePageMode = () => {
    pageMode.value = pageMode.value === pageModeOptions.value[0].value ? pageModeOptions.value[1].value : pageModeOptions.value[0].value;
};

const editTitle = () => {
    editingTitle.value = true;
};

const saveTitle = (event) => {
    editingTitle.value = false;
    emit('update:title', event.target.value);
};

defineExpose({
    selectPage,
    jumpToCategory,
    updatePageCategory
});
</script>